import React, { useEffect, useMemo, useState } from "react";
import { Controller, DeepMap, FieldError, useForm } from "react-hook-form";
import * as Scroll from "react-scroll";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/router";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Edit as EditIcon, Save as SaveIcon } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { SubHeader } from "src/apps/commons/dataDisplays";
import { FileUpload } from "src/apps/commons/fileUploads";
import { yupTrimToNull } from "src/utils/stringUtils";
import { IRemyndProduct } from "../../remyndProduct/types";
import {
  IRemyndManual,
  IRemyndManualSaveReq,
  IRemyndManualSaveReqContainer
} from "../types";
import { paths } from "../utils";
import SelectableRemyndProductList from "./SelectableRemyndProductList";
import SelectedRemyndProductList from "./SelectedRemyndProductList";

const scroll = Scroll.animateScroll;

const useStyles = makeStyles(_ => ({
  button: {
    paddingTop: "16px"
  },
  layoutButton: {
    flex: "1 1 auto"
  },
  skeleton: {
    display: "inline-flex",
    height: "72px",
    width: "100%"
  }
}));

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  isShow: boolean;
  data?: IRemyndManual;
  onSubmit: (req: IRemyndManualSaveReqContainer) => void;
}

type StateTypes = {
  fileId?: string;
  productDetails: IRemyndProduct[];
};

const schema = yup.object().shape({
  nameTh: yup.string().transform(yupTrimToNull).required("กรุณาระบุ ชื่อ (TH)"),
  nameEn: yup.string().transform(yupTrimToNull).required("กรุณาระบุ ชื่อ (EN)"),
  descTh: yup.string().transform(yupTrimToNull),
  descEn: yup.string().transform(yupTrimToNull),
  link: yup.string().transform(yupTrimToNull),
  active: yup.boolean()
});

const Form: React.FC<Props> = props => {
  const classes = useStyles();
  const router = useRouter();
  const { isLoading, isSaving, data, onSubmit, isShow } = props;
  const [state, setState] = useState<StateTypes>({
    fileId: data?.fileId,
    productDetails: data?.products || []
  });

  const defaultFormData = useMemo(
    () => ({
      nameTh: data?.nameTh ?? "",
      nameEn: data?.nameEn ?? "",
      descTh: data?.descTh ?? "",
      descEn: data?.descEn ?? "",
      link: data?.link ?? "",
      active: data?.active ?? true
    }),
    [data]
  );
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<IRemyndManualSaveReq>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const handleTransformAndSubmit = (req: IRemyndManualSaveReq) => {
    onSubmit({
      manual: {
        ...req,
        fileId: state.fileId
      },
      productMaps: state.productDetails.map(d => ({ productId: d.id }))
    });
  };

  const handleInvalid = (error: DeepMap<IRemyndManualSaveReq, FieldError>) => {
    if (error) {
      scroll.scrollTo(0, { delay: 0 });
    }
  };

  const excludeIds = useMemo(
    () => state.productDetails.map(d => d.id),
    [state.productDetails]
  );

  useEffect(() => {
    if (data) {
      reset(defaultFormData);
      setState({
        fileId: data?.fileId,
        productDetails: data?.products || []
      });
    }
  }, [reset, data, defaultFormData]);

  return (
    <form onSubmit={handleSubmit(handleTransformAndSubmit, handleInvalid)}>
      <Grid container alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="nameTh"
              control={control}
              defaultValue={defaultFormData.nameTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.nameTh}
                  helperText={errors.nameTh?.message}
                  InputProps={{ readOnly: isShow }}
                  {...field}
                  label="ชื่อ (TH) *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="nameEn"
              control={control}
              defaultValue={defaultFormData.nameEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.nameEn}
                  helperText={errors.nameEn?.message}
                  InputProps={{ readOnly: isShow }}
                  {...field}
                  label="ชื่อ (EN) *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="descTh"
              control={control}
              defaultValue={defaultFormData.descTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.descTh}
                  helperText={errors.descTh?.message}
                  InputProps={{ readOnly: isShow }}
                  {...field}
                  label="รายละเอียด (TH)"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="descEn"
              control={control}
              defaultValue={defaultFormData.descEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.descEn}
                  helperText={errors.descEn?.message}
                  InputProps={{ readOnly: isShow }}
                  {...field}
                  label="รายละเอียด (EN)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} />

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="link"
              control={control}
              defaultValue={defaultFormData.link}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.link}
                  helperText={errors.link?.message}
                  InputProps={{ readOnly: isShow }}
                  {...field}
                  label="Download Link"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FileUpload
              label={"ไฟล์อัพโหลด"}
              fileId={state.fileId}
              isShow={isShow}
              onChange={fileId =>
                setState(d => ({
                  ...d,
                  fileId: fileId
                }))
              }
            />
          )}
        </Grid>

        <Grid item xs={12}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FormGroup row>
              <FormControlLabel
                control={
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={defaultFormData.active}
                    render={({ field: { value, onChange } }) => (
                      <Box ml={1}>
                        <Checkbox
                          color="primary"
                          checked={value}
                          onChange={onChange}
                          disabled={isSaving || isShow}
                        />
                      </Box>
                    )}
                  />
                }
                label="Active"
              />
            </FormGroup>
          )}
        </Grid>

        {!isShow && (
          <>
            <Grid item xs={12}>
              <SubHeader
                title="เลือกสินค้า เพื่อผูกกับคู่มือ/ไฟล์แนบ"
                marginTop
              />
            </Grid>

            <Grid item xs={12}>
              <SelectableRemyndProductList
                excludeIds={excludeIds}
                onAdd={product =>
                  setState(d => ({
                    ...d,
                    productDetails: [...d.productDetails, product]
                  }))
                }
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <SubHeader
            title={`ผูกกับสินค้า (${state.productDetails.length})`}
            marginTop
          />
        </Grid>

        <Grid item xs={12}>
          <SelectedRemyndProductList
            isShow={isShow}
            products={state.productDetails}
            onDelete={product =>
              setState(d => ({
                ...d,
                productDetails: d.productDetails.filter(
                  f => f.id !== product.id
                )
              }))
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Box mt={2} mb={1}>
            <Divider light />
          </Box>
        </Grid>

        <Grid item className={classes.layoutButton} />

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            onClick={() => router.push(paths.index)}
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
        </Grid>

        <Grid item className={classes.button}>
          {isShow ? (
            <Button
              variant="contained"
              color="primary"
              disabled={isSaving || isLoading}
              href={`${paths.edit}?id=${data?.id}`}
              type="button"
              startIcon={
                isSaving ? (
                  <CircularProgress color="secondary" size={20} />
                ) : (
                  <EditIcon />
                )
              }
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={isSaving || isLoading}
              type="submit"
              startIcon={
                isSaving ? (
                  <CircularProgress color="secondary" size={20} />
                ) : (
                  <SaveIcon />
                )
              }
            >
              Save
            </Button>
          )}
        </Grid>
      </Grid>
    </form>
  );
};

export default Form;
